import { styled } from '@stitches/react';
import { useState } from 'react';
import ReactSlider from 'react-slider';
import { calculateIfRightLabelShouldChangePosition } from './utils';
import { theme } from '../../../tailwind.config';
import { BodyRegular } from '../typography';

const SliderTrack = styled('div', {
  backgroundColor: 'var(--background-color)',
  position: 'relative',
  flexGrow: 1,
  borderRadius: '2px',
  height: '4px',
  cursor: 'pointer',
  top: '9px'
});

const Track = (props, state) => {
  const { key, ...restProps } = props;
  return (
    <SliderTrack
      css={{
        '--background-color':
          state.index === 1
            ? theme.colors.primary['600']
            : theme.colors.neutral['300']
      }}
      key={key}
      {...restProps}
      index={state.index}
    />
  );
};

const SliderThumb = styled('div', {
  display: 'block',
  position: 'relative',
  width: '20px',
  height: '20px',
  backgroundColor: 'white',
  border: `2px solid ${theme.colors.primary[600]}`,
  boxShadow: '0px 2px 15px rgba(0, 0, 0, 0.05)',
  borderRadius: '10px',
  cursor: 'pointer',
  '&:focus': {
    outline: 'none',
    border: `2px solid ${theme.colors.primary[600]}`
  }
});

type SliderType = {
  step: number;
  minStepsBetweenThumbs?: number;
  max: number;
  min: number;
  name: string;
  onValueCommit?: (value: number[] | number) => void;
  label?: string;
  value?: number[];
  setValue?: (value: number[]) => void;
  variant: 'controlled' | 'uncontrolled';
  defaultValue?: number[];
  disabled?: boolean;
  fixedLabels?: boolean;
  /**
   * Hides the floating thumb value label (and the bottom margin it needs)
   * for callers that render the value elsewhere, without forcing the
   * fixedLabels end-pill layout.
   */
  shouldHideThumbLabel?: boolean;
  /**
   * Formats the value shown in the thumb labels and fixed-label pills, e.g.
   * to prefix a gameweek number with "GW". Defaults to the raw number.
   */
  formatLabel?: (value: number) => string;
  /**
   * Extra classes for the fixed-label pills, for callers that need a
   * different size or type treatment from the default.
   */
  pillClassName?: string;
};

const RangeSlider = ({
  step,
  minStepsBetweenThumbs,
  max,
  min,
  name,
  onValueCommit,
  label,
  value,
  setValue,
  variant,
  defaultValue,
  disabled,
  fixedLabels,
  shouldHideThumbLabel = false,
  formatLabel = (labelValue) => String(labelValue),
  pillClassName = ''
}: SliderType) => {
  const isControlledComponent = variant === 'controlled';
  const [localState, setLocalState] = useState<number[] | undefined>(
    defaultValue
  );
  const shouldChangeRightLabelPosition =
    calculateIfRightLabelShouldChangePosition({
      isControlledComponent,
      value,
      localState,
      max,
      min,
      step
    });

  const Thumb = (props, state) => {
    const { key, ...restProps } = props;
    return (
      <SliderThumb
        className="relative"
        data-testid={`${name}${state.index === 1 ? '-2' : ''}-thumb`}
        id={`${name}${state.index === 1 ? '-2' : ''}-thumb`}
        key={key}
        {...restProps}
      >
        {!fixedLabels && !shouldHideThumbLabel && (
          <BodyRegular
            textColor="text-neutral-50 bg-primary-600"
            className={`text-center flex justify-center top-6 left-[1px] rounded-lg absolute inline-flex w-[40px] transition-transform ${
              shouldChangeRightLabelPosition && state.index === 1
                ? 'translate-x-2'
                : shouldChangeRightLabelPosition && state.index === 0
                ? '-translate-x-8'
                : '-translate-x-1/3'
            }`}
          >
            {formatLabel(state.valueNow)}
          </BodyRegular>
        )}
      </SliderThumb>
    );
  };

  return (
    <div className={fixedLabels ? 'flex' : shouldHideThumbLabel ? '' : 'mb-14'}>
      {label && (
        <label
          htmlFor={name}
          className="inline-flex text-base font-normal text-neutral-800 mb-2"
        >
          {label}
        </label>
      )}
      {fixedLabels && (
        <BodyRegular
          textColor={`w-[42px] text-center text-neutral-50 bg-primary-600 rounded-lg px-2 mr-2 ${pillClassName}`}
        >
          {formatLabel(
            (isControlledComponent ? value?.[0] : localState?.[0]) ?? min
          )}
        </BodyRegular>
      )}
      <ReactSlider
        disabled={disabled}
        className={`horizontal-slider ${
          disabled ? 'opacity-50' : 'opacity-100'
        } ${fixedLabels ? 'w-full' : ''} ${shouldHideThumbLabel ? 'h-5' : ''}`}
        defaultValue={[0, 100]}
        ariaLabel={['Lower thumb', 'Upper thumb']}
        ariaValuetext={(state) => `Thumb value ${state.valueNow}`}
        renderThumb={Thumb}
        renderTrack={Track}
        onAfterChange={onValueCommit}
        pearling
        max={max}
        min={min}
        name={name}
        step={step}
        minDistance={minStepsBetweenThumbs}
        value={isControlledComponent ? value : localState}
        onChange={(val) => {
          if (isControlledComponent) {
            setValue && setValue(val);
          } else {
            setLocalState(val);
          }
        }}
      />
      {fixedLabels && (
        <BodyRegular
          textColor={`w-[42px] text-center text-neutral-50 bg-primary-600 rounded-lg px-2 ml-2 ${pillClassName}`}
        >
          {formatLabel(
            (isControlledComponent ? value?.[1] : localState?.[1]) ?? max
          )}
        </BodyRegular>
      )}
    </div>
  );
};

export default RangeSlider;
